import { useId } from 'react';

import ModalActions from 'components/task-item/modal-actions';
import ModalDeleteTask, { deleteTaskModalId } from 'components/task-item/modal-actions/delete-task';
import { TaskItemProps, useTaskItem } from 'components/task-item/useTaskItem';
import Button from 'components/ui/button';
import { FOCO_VISIVEL } from 'utils/foco';

import { acoesDaMinhaRealizacao } from '../acoes-da-minha-realizacao';
import { pilulaDaRealizacao, progressoDaTrilha } from '../pilula-da-realizacao';
import { FaseDoCiclo } from '../realizacoes-do-semestre';
import { useAvisoDaPilula } from '../useAvisoDaPilula';
import AcoesDoParticipante from './AcoesDoParticipante';
import { etapasDaAgenda } from './AgendaDaRealizacao';
import CabecalhoDaRealizacao, { autorDaRealizacao } from './CabecalhoDaRealizacao';
import CamposDaRealizacao, { percentualDaAtuacao } from './CamposDaRealizacao';
import { alternarMaisInformacoes, BotaoMaisInformacoes, PainelMaisInformacoes } from './MaisInformacoes';
import TrilhaDaRealizacao from './TrilhaDaRealizacao';

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[10.5px] font-bold uppercase tracking-[0.04em] hover:underline ${FOCO_VISIVEL}`;

export interface CartaoDaMinhaRealizacaoProps {
  data: any;
  /** Fase mensal em que a realização foi cadastrada (o `cycle` que a página antiga passava ao `TaskItem`). */
  fase: FaseDoCiclo | undefined;
  rotuloDaFase: string;
  playerInfo: any;
  /** `realizacaoEditavel` (regra `taskEditable` de hoje). */
  editavel: boolean;
  /** `podeAtualizarIndicador` (regra `taskCanUpdateIndicator` de hoje). */
  podeAtualizarIndicador: boolean;
  onAtualizarIndicador: () => void;
  onSolicitarAutorizacao: NonNullable<TaskItemProps['onOpenModalSendMessage']>;
  /** Depois de excluir ou quando o "Mais informações" pede a leitura completa: a página relê o ciclo. */
  onRecarregar: () => void;
  /** Pílula do cartão (RDV-14), avisada sempre que muda, para as pílulas do topo da página. */
  onPilula?: (pilula: string) => void;
}

/**
 * Cartão da realização própria (RDV-16 a RDV-21) sobre o mesmo modelo do `TaskItem`: as ações e os modais seguem as
 * regras de hoje (RDN-10, RDN-11).
 */
export default function CartaoDaMinhaRealizacao({
  data,
  fase,
  rotuloDaFase,
  playerInfo,
  editavel,
  podeAtualizarIndicador,
  onAtualizarIndicador,
  onSolicitarAutorizacao,
  onRecarregar,
  onPilula,
}: CartaoDaMinhaRealizacaoProps) {
  const modelo = useTaskItem({
    data,
    cycle: fase as TaskItemProps['cycle'],
    playerInfo,
    editable: editavel,
    canUpdateIndicator: podeAtualizarIndicador,
    onUpdateIndicator: onAtualizarIndicador,
    onOpenModalSendMessage: onSolicitarAutorizacao,
    onDelete: onRecarregar,
    onReload: onRecarregar,
  });
  const acoes = acoesDaMinhaRealizacao(modelo);
  const etapas = etapasDaAgenda(modelo);
  const pilula = pilulaDaRealizacao(etapas);
  const chave: string | undefined = modelo.user?._id;
  const percentual = chave ? percentualDaAtuacao(modelo.getUserTeam(chave)?.percent) : undefined;
  const gestorQueAutoriza = data.managers_to_approve?.[0];

  const tituloId = useId();
  const detalhesId = useId();

  useAvisoDaPilula(pilula, onPilula);

  return (
    <article aria-labelledby={tituloId} className="overflow-hidden rounded-jj-lg bg-brand-white shadow-jj-01">
      {acoes.solicitarAutorizacao && (
        <div className="flex flex-wrap items-center gap-3 bg-brand-yellow-light px-5 py-3.5">
          <p className="m-0 flex-1 text-xs text-brand-blue-dark">
            {`Número de participantes pendentes de autorização pelo gestor ${gestorQueAutoriza.nome_guerra}`}
          </p>
          <Button
            variant="solido"
            size="sm"
            title={`Solicitar liberação para ${gestorQueAutoriza.nome_guerra} (${gestorQueAutoriza.chave})`}
            onClick={() =>
              onSolicitarAutorizacao({
                managerName: gestorQueAutoriza.nome_guerra,
                participantCount: data?.participants_approval?.total,
                taskTitle: data.title,
                managerId: gestorQueAutoriza.chave,
              })
            }
          >
            Solicitar autorização
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-3.5 px-5 pb-4 pt-[18px]">
        <CabecalhoDaRealizacao
          tituloId={tituloId}
          titulo={data.title}
          autor={autorDaRealizacao(data)}
          propria
          rotuloDaFase={rotuloDaFase}
          cadastradaEm={data.created}
          pilula={pilula}
          pontos={modelo.points}
        />
        <TrilhaDaRealizacao etapas={etapas} progresso={progressoDaTrilha(etapas)} />
        <CamposDaRealizacao data={data} percentual={percentual} />
        <div className="text-[11.5px] text-content-soft empty:hidden">{modelo.evaluationData}</div>
        <PainelMaisInformacoes
          id={detalhesId}
          modelo={modelo}
          etapas={etapas}
          acoesDoParticipante={pessoa => <AcoesDoParticipante pessoa={pessoa} modelo={modelo} />}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 border-t border-surface-200 bg-surface-100 px-5 py-3">
        {acoes.editar && (
          <button type="button" onClick={modelo.handleEdit} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
            Editar
          </button>
        )}
        {acoes.excluir && (
          // SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
          <button
            type="button"
            onClick={() => modelo.showModal(deleteTaskModalId(data._id))}
            className={`${ACAO_DE_TEXTO} text-urgent`}
          >
            Excluir
          </button>
        )}
        {acoes.atualizarIndicador && (
          <button type="button" onClick={onAtualizarIndicador} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
            Atualizar indicador
          </button>
        )}
        <BotaoMaisInformacoes aberto={modelo.oppened} controla={detalhesId} onClick={() => alternarMaisInformacoes(modelo)} />
      </div>

      <ModalActions
        callback={modelo.callBackActions}
        id={data._id}
        status={modelo.status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={modelo.validateTeamMember}
        activeEvaluationStep={modelo.activeEvaluationStep as number}
        bonus={modelo.bonus}
        userToChange={modelo.userToChange}
        ecoa={modelo.ecoa}
      />

      <ModalDeleteTask
        taskId={data._id}
        requiresReason={modelo.deleteNeedsReason}
        onDeleted={onRecarregar}
        onRefused={onRecarregar}
      />
    </article>
  );
}
